/* eslint-disable */
// @flow
/** @jsx jsx */

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { jsx } from '@emotion/core';
import { Query } from 'react-apollo';
import cx from 'classnames';

import type { GridItem } from '../../types';
import GridApi from '../../components/GridApi';
import PageContainer from '../../components/PageContainer';
import ActionsHeader from '../../components/ActionsHeader';
import ModuleQuery from '../../components/ModuleQuery';
import Row from '../../components/Row';
import Col from '../../components/Col';
import Notifications from '../../components/Notifications';
import ContentChecker from '../../components/ContentChecker';
import FullSizeCenteredContent from '../../components/FullSizeCenteredContent';
import Loader from '../../components/Loading';
import BlockingStatus from '../../components/BlockingStatus';
import GridApiController from '../../controllers/GridApiController';
import userSettingsQuery from '../../graphql/queries/userSettings.query';
import moduleDataQuery from '../../graphql/queries/itemHistoryData.query';
import ActionsOrganizer from './components/ActionsOrganizer';
import { getDefaultUserSettings } from '../../utils'
import { MobileView, useMobileMediaQuery } from '../../config/mediaQuery';
import ModuleMenuBar from '../../components/ModuleMenuBar';
import { ActionsContext } from '../../context/actions.context';
import useMenuHandler from '../../hooks/useMenuHandler';

type Props = {
  isFullPage?: boolean,
  selectedRow?: GridItem,
  showGroupPanel?: boolean,
  paginated?: boolean,
  accountName: String;
};

const App = (props: Props) => {
  const moduleName = 'itemHistory';

  const menuRef = useRef();
  const menuButtonRef = useRef();
  const { allScreens = [] } = useContext(ActionsContext);

  const isMobile = useMobileMediaQuery();
  const [displayMenu, setDisplayMenu] = useState(false);
  const [handlerAdded, setHandlerAdded] = useState(false);
  
  // State to track the displayed view name for UI updates
  // - undefined: initial state, will sync from query on first load
  // - null: user selected "Default View"
  // - string: user selected a named view
  const [displayedViewName, setDisplayedViewName] = useState(undefined);

  const changeDisplayMenu = (status) => {
    setDisplayMenu(status);
  }

  const {
    isFullPage = false,
    showGroupPanel = true,
    paginated = false,
    accountName
  } = props;

  const handler = useCallback((e) => {
    if (menuRef.current
      && menuButtonRef.current
      && !menuRef.current.contains(e.target)
      && !menuButtonRef.current.contains(e.target)
    ) {
      setDisplayMenu(value => {
        if (value) {
          return false;
        }
        return value;
      });
    }
  }, []);

  useMenuHandler(handler, allScreens.length === 1, setHandlerAdded);
  
  // Handler for when a view is loaded - just updates the UI display name
  // The actual view settings are applied by onApplyViewSettings in ActionsHeader
  const handleViewLoaded = (viewName) => {
    setDisplayedViewName(viewName);
  };

  return (
    <PageContainer
      className={cx({
        'page--is-full-page': isFullPage,
        'page--is-nested': !isFullPage,
      })}
      isFullPage={isFullPage}
    >
      <ModuleQuery
        query={moduleDataQuery}
        variables={{
          moduleName: moduleName,
          selectedRow: props.selectedRow,
        }}
      >
        {({
          data: moduleData,
          loading: moduleLoading,
          refetch: moduleRefetch,
          refetching: moduleRefetching,
          refetched: moduleRefetched,
        }) => (
          <Query
            query={userSettingsQuery}
            variables={{
              moduleName: moduleName,
            }}
          >
            {({ data: dataGrid, loading }: any) => {
              if (loading || !dataGrid) {
                return (
                  <FullSizeCenteredContent>
                    <Loader />
                  </FullSizeCenteredContent>
                );
              }

              // Get view name from query - null means Default View
              const queryViewName = dataGrid && dataGrid.userSettings && dataGrid.userSettings.viewName
                ? dataGrid.userSettings.viewName
                : null;

              // Build userSettings based on whether we have a named view or Default View
              // IMPORTANT: When viewName is null (Default View), we should NOT pass
              // the server-returned columns/sorting/groups/frozenColumns/filterDefinition
              // because those are empty/stale for Default View. Instead, we let
              // GridApiController fall back to the default settings.
              //
              // This fixes the conditional-clear antipattern where loading Default View
              // on initial page refresh would not apply default settings correctly.
              let userSettings;
              if (queryViewName) {
                // Named view - use server-returned settings
                userSettings = dataGrid && dataGrid.userSettings
                  ? {
                      columnsMapping: dataGrid.userSettings.columns,
                      frozenColumns: dataGrid.userSettings.frozenColumns,
                      groups: dataGrid.userSettings.groups,
                      sorting: dataGrid.userSettings.sorting,
                      filterDefinition: dataGrid.userSettings.filterDefinition,
                    }
                  : {};
              } else {
                // Default View - don't pass view-specific settings
                // Let GridApiController use defaultColumns, defaultSorting, defaultGroupPanels
                userSettings = {};
              }

              const defaultUserSettings = getDefaultUserSettings(dataGrid)
              
              // Only sync displayedViewName from query on initial load (when displayedViewName is undefined)
              // After initial load, displayedViewName is managed entirely by user selection
              if (displayedViewName === undefined) {
                // Use setTimeout to avoid setState during render
                setTimeout(() => { setDisplayedViewName(queryViewName); }, 0);
              }
              
              // Determine what to show in the UI:
              // - If displayedViewName is undefined (initial load), use queryViewName
              // - Otherwise use displayedViewName (which could be null for Default View)
              const currentViewNameForUI = displayedViewName === undefined ? queryViewName : displayedViewName;

              return (
                <GridApiController
                  data={moduleData.itemHistoryContent}
                  loading={loading || moduleLoading}
                  refetching={moduleRefetching}
                  refetched={moduleRefetched}
                  refetch={moduleRefetch}
                  defaultColumns={defaultUserSettings.defaultColumns}
                  defaultSorting={defaultUserSettings.defaultSorting}
                  defaultGroupPanels={defaultUserSettings.defaultGroupPanels}
                  paginated={paginated}
                  isMobile={isMobile}
                  {...userSettings}
                >
                  {({
                    selectedItems,
                    gridUpdated,
                    collection,
                    columnsMapping,
                    currentItem,
                    onGridUpdated,
                    onGridRef,
                    onGridWillUnmount,
                    onColSorted,
                    onColResized,
                    onColDragged,
                    onColFreezed,
                    onRowFreezed,
                    onRowSelected,
                    onColumnsMappingChange,
                    onCurrentItemChanged,
                    onGroupPanelChanged,
                    onClearSelection,
                    onNotesSaved,
                    saveStatus,
                    onViewSaved,
                    onViewReset,
                    onApplyViewSettings,
                    grid,
                    userViewState,
                    refetching,
                    hasAllColumnsHidden,
                    frozenColumns,
                    refetch,
                    isLoading,
                    isRefetching,
                    isCollapseLoading,
                    setCollapseLoading,
                    hasDataToDisplay,
                    playResetAnimation,
                    handleResetAnimation,
                    setFlexGridFilter,
                    getGridFilterDefinition,
                    clearInternalFilters,
                    toggleMultiSort,
                    multiColumnSort
                  }) => (
                    <React.Fragment>
                      {/* Show loading indicator during collapse/expand operations */}
                      {isCollapseLoading && (
                        <BlockingStatus
                          excludeHeader
                          active
                          container={document.body}
                          text="Processing..."
                        />
                      )}
                      <Notifications saveStatus={saveStatus} />
                      <ActionsHeader
                        grid={grid}
                        collection={collection}
                        defaultColumnsMapping={defaultUserSettings.defaultColumns}
                        columnsMapping={columnsMapping}
                        selectedItems={selectedItems}
                        moduleName={moduleName}
                        saveStatus={saveStatus}
                        loading={isLoading || isRefetching}
                        onViewSaved={onViewSaved}
                        onViewReset={onViewReset}
                        onApplyViewSettings={onApplyViewSettings}
                        onNotesSaved={onNotesSaved}
                        onColumnsMappingChange={onColumnsMappingChange}
                        userViewState={userViewState}
                        refetch={refetch}
                        hasAllColumnsHidden={hasAllColumnsHidden}
                        hasDataToDisplay={hasDataToDisplay}
                        frozenColumns={frozenColumns}
                        currentItem={currentItem}
                        defaultSettings={defaultUserSettings}
                        handleResetAnimation={handleResetAnimation}
                        useLegacyColumnsManager={true}
                        toggleMultiSort={toggleMultiSort}
                        multiColumnSort={multiColumnSort}
                        // Multiple saved views feature props
                        useMultipleViews={true}
                        currentViewName={currentViewNameForUI}
                        onViewLoaded={handleViewLoaded}
                        getGridFilterDefinition={getGridFilterDefinition}
                      >
                        {actions => {
                          const { ColumnsAction, isOneRowSelected } = actions;
                          return (
                            <>
                              <MobileView>
                                <div className='module-title'>{accountName}</div>
                                <ModuleMenuBar
                                  moduleRefetch={moduleRefetch}
                                  changeDisplayMenu={changeDisplayMenu}
                                  ColumnsAction={ColumnsAction}
                                  displayMenu={displayMenu}
                                  menuButtonRef={menuButtonRef}
                                  clearInternalFilters={clearInternalFilters}
                                />
                              </MobileView>
                              <div ref={menuRef}>
                                <Row
                                  css={{
                                    justifyContent: 'space-between',
                                    padding: '0 10px',
                                    ...(isMobile && {
                                      position: 'absolute',
                                      bottom: 0,
                                      boxShadow:
                                        '0px -4px 4px 0px #ACACAC40',
                                      zIndex: 100,
                                      background: '#fff',
                                      borderRadius: '8px 8px 0px 0px',
                                      width: '100%',
                                      padding: '10px 20px',
                                      display: displayMenu || isOneRowSelected
                                        ? 'flex'
                                        : 'none',
                                      justifyContent: 'center',
                                      height: (displayMenu && '189px') || (isOneRowSelected && '210px')
                                    }),
                                  }}
                                >
                                  <Col>
                                    <ActionsOrganizer
                                      minHeight="80px"
                                      clearInternalFilters={clearInternalFilters}
                                      {...actions}
                                      displayRowMenu={isOneRowSelected}
                                      displayMenu={displayMenu}
                                      toggleMultiSort={toggleMultiSort}
                                    />
                                  </Col>
                                </Row>
                              </div>
                            </>
                          )
                        }}
                      </ActionsHeader>
                      <ContentChecker
                        isLoading={isLoading}
                        isRefetching={isRefetching}
                        hasAllColumnsHidden={hasAllColumnsHidden}
                        hasDataToDisplay={hasDataToDisplay}
                        collection={collection}
                      >
                        {() => (
                          <React.Fragment>
                            <GridApi
                              collection={collection}
                              columnsMapping={columnsMapping}
                              onRowSelected={onRowSelected}
                              onColSorted={onColSorted}
                              onColResized={onColResized}
                              onColDragged={onColDragged}
                              onColFreezed={onColFreezed}
                              onRowFreezed={onRowFreezed}
                              onGridUpdated={onGridUpdated}
                              onGridWillUnmount={onGridWillUnmount}
                              onCurrentItemChanged={onCurrentItemChanged}
                              onGroupPanelChanged={onGroupPanelChanged}
                              onClearSelection={onClearSelection}
                              ref={onGridRef}
                              refetching={isRefetching}
                              hasAllColumnsHidden={hasAllColumnsHidden}
                              hasDataToDisplay={hasDataToDisplay}
                              showFrozenBars={false}
                              isFullPage={isFullPage}
                              showGroupPanel={showGroupPanel}
                              paginated={paginated}
                              playResetAnimation={playResetAnimation}
                              setFlexGridFilter={setFlexGridFilter}
                              menuRef={menuRef}
                              isMobile={isMobile}
                              moduleName={moduleName}
                              handlerAdded={handlerAdded}
                              multiColumnSort={multiColumnSort}
                            />
                          </React.Fragment>
                        )}
                      </ContentChecker>
                    </React.Fragment>
                  )}
                </GridApiController>
              );
            }}
          </Query>
        )}
      </ModuleQuery>
    </PageContainer>
  );
}

export default App;